import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { Popup } from 'src/components/Popup/Popup';
import { DateInputNew } from 'src/domains/layouts/shared/formComponents/dateInput/DateInputNew';
import { useCommon } from 'src/AppState/Common';
import { ReportsFilterV2State } from 'src/components/Reports/ReportsFilter/ReportsFilterV2.state';

const ReportFormInner = observer('ReportFormInner', (): JSX.Element | null => {
    const common = useCommon();
    const reportsState = ReportsFilterV2State.get(common);
    const formType = reportsState.reportFormState.getFormType(reportsState.currentReportType);

    const lastDayInput = (
        <div
            className='element checkbox'
            data-test='include-last-day'
        >
            <label>
                <input
                    type='checkbox'
                    onChange={reportsState.onChangeIncludeLastDayHandler}
                />
                <span>Include Last Day</span>
            </label>
        </div>
    );

    const lastDayGrandedInput = (
        <div
            className='element checkbox'
            data-test='include-last-day'
        >
            <label>
                <input
                    type='checkbox'
                    onChange={reportsState.onChangeIncludeLastDayGarantedHandler}
                />
                <span>Include Last Day</span>
            </label>
        </div>
    );

    switch (formType) {
        case 'restrainedRange':
            // max date restricted
            return (
                <div className='formData'>
                    <div className='element'>
                        <div className='row'>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateStartForm}
                                    selected={reportsState.reportFormState.dateStartForm.value}
                                    label='from'
                                />
                            </div>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateEndForm}
                                    selected={reportsState.reportFormState.dateEndForm.value}
                                    label='to'
                                />
                            </div>
                        </div>
                    </div>
                    {lastDayInput}
                </div>
            );
        case 'unRestrainedRange':
            // maxDate = null
            return (
                <div className='formData'>
                    <div className='element'>
                        <div className='row'>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateStartForm}
                                    selected={reportsState.reportFormState.dateStartForm.value}
                                    label='from'
                                />
                            </div>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateEndForm}
                                    selected={reportsState.reportFormState.dateEndForm.value}
                                    label='to'
                                />
                            </div>
                        </div>
                    </div>
                    {lastDayInput}
                </div>
            );
        case 'lastBetAfter':
            return (
                <div className='formData'>
                    <div className='element'>
                        <div className='row'>
                            <DateInputNew
                                stateValue={reportsState.reportFormState.dateStartForm}
                                selected={reportsState.reportFormState.dateStartForm.value}
                                label='Last bet after'
                            />
                        </div>
                    </div>
                    {lastDayInput}
                </div>
            );
        case 'grantedRange':
            return (
                <div className='formData'>
                    <div className='element'>
                        <div className='row'>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateStartForm}
                                    selected={reportsState.reportFormState.dateStartForm.value}
                                    label='Granted start date'
                                />
                            </div>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateEndForm}
                                    selected={reportsState.reportFormState.dateEndForm.value}
                                    label='Granted end date'
                                />
                            </div>
                        </div>
                        {lastDayGrandedInput}
                        <div className='row'>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.claimedStartDateForm}
                                    selected={reportsState.reportFormState.claimedStartDateForm.value}
                                    label='Claimed start date'
                                />
                            </div>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.claimedEndDateForm}
                                    selected={reportsState.reportFormState.claimedEndDateForm.value}
                                    label='Claimed end date'
                                />
                            </div>
                        </div>
                    </div>
                    {lastDayInput}
                </div>
            );
        case 'filteredWithDate':
            const label = reportsState.currentReportType === 'trading' ? 'Significant amount' : 'Limit the list';
            const parameter =
                reportsState.currentReportType === 'trading' ? 'params.significantAmount' : 'params.limit';
            return (
                <div className='formData'>
                    <div className='element'>
                        <div className='row'>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateStartForm}
                                    selected={reportsState.reportFormState.dateStartForm.value}
                                    label='from'
                                />
                            </div>
                            <div className='col-6'>
                                <DateInputNew
                                    stateValue={reportsState.reportFormState.dateEndForm}
                                    selected={reportsState.reportFormState.dateEndForm.value}
                                    label='to'
                                />
                            </div>
                        </div>
                    </div>
                    <div className='element empty hasLabel'>
                        <input
                            type='text'
                            name={parameter}
                        />
                        <label>{label}</label>
                    </div>
                    {lastDayInput}
                </div>
            );
    }

    return null;
});

export const ReportForm = observer('ReportForm', () => {
    const common = useCommon();
    const reportsState = ReportsFilterV2State.get(common);

    const reportForm = <ReportFormInner />;

    return (
        <Popup
            title='Specify report parameters'
            className='reports-filter-report-params-modal'
            active={reportsState.displayReportParamsPopup}
            closeButton={true}
            overlayClose={true}
            onClose={reportsState.hideReportParamsForm}
        >
            {reportForm}

            <div className='buttons'>
                <button
                    className='btn'
                    onClick={() => reportsState.onRunReportHandler(true)}
                >
                    Run report
                </button>
            </div>
        </Popup>
    );
});
